// Phase 2.10 — Compact pill row for finding framework / standard mappings.
// Read-only; does NOT affect scoring. See src/lib/findingFrameworkPills.ts.
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { FrameworkPill } from "@/lib/findingFrameworkPills";

interface Props {
  pills: FrameworkPill[];
  /** Max pills to render inline; the rest collapse into "+N" with tooltip. */
  max?: number;
  className?: string;
}

const FAMILY_CLASS: Record<string, string> = {
  NIST: "bg-blue-500/10 text-blue-700 border-blue-500/30 dark:text-blue-300",
  GSMA: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30 dark:text-emerald-300",
  "3GPP": "bg-violet-500/10 text-violet-700 border-violet-500/30 dark:text-violet-300",
  ENISA: "bg-amber-500/10 text-amber-700 border-amber-500/30 dark:text-amber-300",
  ETSI: "bg-cyan-500/10 text-cyan-700 border-cyan-500/30 dark:text-cyan-300",
  ISO: "bg-muted/60 text-muted-foreground border-border",
  RFC: "bg-muted/60 text-muted-foreground border-border",
  IEEE: "bg-muted/60 text-muted-foreground border-border",
  Other: "bg-muted/60 text-muted-foreground border-border",
};

export const FindingFrameworkPills: React.FC<Props> = ({ pills, max = 99, className }) => {
  if (!pills || pills.length === 0) return null;
  const shown = pills.slice(0, max);
  const overflow = pills.slice(max);

  return (
    <div className={`flex flex-wrap gap-1 ${className ?? ""}`}>
      {shown.map((p) => (
        <Badge
          key={p.label}
          variant="outline"
          className={`text-[10px] px-1.5 py-0 font-normal ${FAMILY_CLASS[p.family] ?? FAMILY_CLASS.Other}`}
        >
          {p.label}
        </Badge>
      ))}
      {overflow.length > 0 && (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                className="text-[10px] px-1.5 py-0 font-normal bg-muted/40 text-muted-foreground border-border cursor-help"
              >
                +{overflow.length} more
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <div className="flex flex-col gap-0.5 text-xs">
                {overflow.map((p) => (
                  <span key={p.label}>{p.label}</span>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};
